// Second sign-in step: the 6 digit code from the user's authenticator app.
// Shown by Login and AdminLogin in place of the email/password form once the
// password was accepted but the account has two step sign in on (see
// utils/mfa.ts).

import { useState, type FormEvent } from 'react';
import { finishSignInMfa, mfaErrorText, type MfaChallenge } from '../utils/mfa';

function IconPhone() {
  return (
    <svg width="17" height="17" viewBox="0 0 20 20" fill="none">
      <rect x="5.5" y="2" width="9" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8.5 15h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export function MfaCodeStep({ challenge, onCancel, onSignedIn }: {
  challenge:   MfaChallenge;
  onCancel:    () => void;
  onSignedIn?: () => void;
}) {
  const [code,  setCode]  = useState('');
  const [busy,  setBusy]  = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!/^\d{6}$/.test(code.trim())) { setError('Enter the 6 digit code.'); return; }
    setBusy(true); setError(null);
    try {
      await finishSignInMfa(challenge, code);
      onSignedIn?.();
      // AuthContext takes over from here; keep the spinner until it redirects.
    } catch (err) {
      setError(mfaErrorText(err));
      setCode('');
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="auth-form">
      <p style={{ fontSize: 13, color: 'var(--text-2)', margin: 0, lineHeight: 1.5 }}>
        Open your authenticator app and enter the 6 digit code for <strong style={{ color: 'var(--text-1)' }}>DiaWolt</strong>.
      </p>

      {error && <div className="error-msg" style={{ marginBottom: 0 }}>{error}</div>}

      <div className="auth-field">
        <IconPhone />
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          aria-label="Sign in code"
          placeholder="6 digit code"
          maxLength={6}
          value={code}
          onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
          autoFocus
          disabled={busy}
          style={{ letterSpacing: code ? '0.3em' : undefined, fontFamily: code ? 'var(--font-num)' : undefined }}
        />
      </div>

      <button type="submit" className="btn btn-primary btn-full" disabled={busy || code.length !== 6} style={{ marginTop: 4 }}>
        {busy ? <><span className="spinner" style={{ borderTopColor: 'white' }} /> Checking…</> : 'Verify and Sign In'}
      </button>

      <button type="button" className="line-item-link-btn" onClick={onCancel} disabled={busy}
        style={{ fontSize: 12.5, alignSelf: 'flex-start' }}>
        Back
      </button>
    </form>
  );
}
